import { LitElement, html, css } from 'lit';
import {eplibMixin} from './eplib-mixin';
import { BootstrapCSS } from '../resources/css/bootstrap.css';
import { EplibStyles } from '../resources/css/style.css.js';

/**
 * Displays a pagination control from which the user can select a page to view
 * from a multi-page collection of resources. To determine the number of pages,
 * `eplib-paginate` listens for the `eplib-results-received` event.
 * If the user clicks on one of the page indicators, an `eplib-load` event is emitted,
 * which should cause the connected `eplib-load` element to refresh.
 *
 * @fires eplib-load - Fires when user selects new page to show
 * @fires eplib-paginator-ready - lets other components know we are connected and ready to do pagination
 *
 * @listens eplib-results-received - When received, recalculates page ranges to display according to the parameters received
 *
 * Adapted from the pb-paginate component of tei-publisher-components
 */
export class EplibPaginate extends eplibMixin(LitElement) {
    static get properties() {
        return {
            ...super.properties,
            /**
             * total number of pages
             */
            total: {
                type: Number,
                reflect: true
            },
            /**
             * Optional extra info about total, such as total number of hits
             * in KWIC results.
             */
            listDescription: {
                type: String,
                attribute: 'list-description'
            },
            /**
             * Optional extra info about what we are paginating through, e.g.
             * hits, documents, annotations, etc.
             */
            totalDescription: {
              type: String,
              attribute: 'total-description'
          },
            /**
             * start page
             */
            start: {
                type: Number,
                reflect: true
            },
            /**
             * amount of entries per page
             */
            perPage: {
                type: Number,
                attribute: 'per-page'
            },
            /**
             * the current page
             */
            page: {
                type: Number
            },
            /**
             * the amount of pages
             */
            pageCount: {
                type: Number,
                attribute: 'page-count'
            },
            /**
             * how many page numbers to show in paginator navigation
             */
            range: {
                type: Number
            },
            /**
             * todo:
             */
            pages: {
                type: Array
            },
            /**
             * channel to use for communicating with containing element
             */
            channel: {
              type: String
            },
            /**
             * on first page so disable previous page navigation
             */
            _disablePrevious: {
                type: String
            },
            /**
             * on last page so disable next page navigation
             */
            _disableNext: {
                type: String
            }
        };
    }

    constructor() {
        super();
        this.total = 0;
        this.start = 1;
        this.perPage = 10;
        this.page = 1;
        this.pageCount = 10;
        this.range = 5;
        this.pages = [];
        this._disablePrevious = 'disabled';
        this._disableNext = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this.subscribeTo('eplib-results-received', this._refresh.bind(this), [this.channel]);
        this.emitTo('eplib-paginator-ready', {}, [this.channel]);
    }

    render() {
        return html`
            <div class="row">
                <div class="col-md-6">
                    <h5 class="float-start found" style="margin-left: 22px">
                        ${this.start}-${Math.min(this.start + this.perPage - 1, this.total)} of ${ this.total.toLocaleString() } ${this.listDescription}
                        <br/>
                        ${this.totalDescription}
                    </h5>
                </div>
                <div class="col-md-6">
                    <ul class="pagination float-end">
                        <li class="page-item ${this._disablePrevious}" @click="${this._handleFirst}">
                          <a class="material-icons md-16 page-link">first_page</a>
                        </li>
                        <li class="page-item ${this._disablePrevious}" @click="${this._handlePrevious}">
                          <a class="material-icons md-16 page-link">navigate_before</a>
                        </li>
                        ${this.pages.map((item, index) => html`<li class="${item.class} page-item" @click="${() => this._handleClick(index)}">
                          <a class="page-link">${item.label}</a>
                        </li>`)}
                        <li class="page-item ${this._disableNext}" @click="${this._handleNext}">
                          <a class="material-icons md-16 page-link">navigate_next</a>
                        </li>
                        <li class="page-item ${this._disableNext}" @click="${this._handleLast}">
                          <a class="material-icons md-16 page-link">last_page</a>
                        </li>
                    </ul>
                </div>
            </div>
        `;
    }

    static get styles() {
      return [
        BootstrapCSS,
        EplibStyles,
        css`
            :host([total="0"]) {
              display: none;
            }
            :host {
              margin: 0.75rem 0.25rem 0.75rem 0.25rem;
            }
            h5 {
              font-family: "IM Fell English", serif !important;
              line-height: 1.2 !important;
              margin-left: 1rem;
            }
            .page-item {
              cursor: pointer;
            }
            .disabled {
              pointer-events: none;
              opacity: 50%;
            }
      `];
    }

    _update(start, total) {
        if (!total || !start) {
            return;
        }
        this.pageCount = Math.ceil(total / this.perPage);
        this.page = Math.ceil(start / this.perPage);
        let lowerBound = Math.max((this.page - Math.ceil(this.range / 2) + 1), 1);
        let upperBound = Math.min((lowerBound + this.range - 1), this.pageCount);
        lowerBound = Math.max((upperBound - this.range + 1), 1);
        console.log("<eplib-paginate> start: %d, total: %d, perPage: %d, pageCount: %s, page: %d, lower: %d, upper: %d",
            start, total, this.perPage, this.pageCount, this.page, lowerBound, upperBound);
        const pages = [];
        for (let i = lowerBound; i <= upperBound; i++) {
            pages.push({
                label: i,
                "class": i === this.page ? "active" : ""
            });
        }
        this.pages = pages;
    }

    _refresh(ev) {
        this.start = Number(ev.detail.start);
        this.total = ev.detail.count;
        this._update(this.start, this.total);
    }

    _handlePageNavigation(page, direction) {
        this.start = (page - 1) * this.perPage + 1;
        this._disablePrevious = page === 1 ? 'disabled' : '';
        this._disableNext = page === this.pageCount ? 'disabled' : '';
        this.emitTo('eplib-load', {
            "params": {
                "start": this.start,
                "per-page": this.perPage,
                "page": page,
                "direction": direction
            }
        }, [this.channel]);
    }

    _handleClick(index) {
        const currentPage = Number(this.pages[index].label);
        if (currentPage === this.page) { return; } // already there
        const direction = currentPage > this.page ? 'nav-next' : 'nav-prev';
        this._handlePageNavigation(this.pages[index].label, direction);
    }

    _handleFirst() {
        this._handlePageNavigation(1, 'nav-prev');
    }

    _handleLast() {
        this._handlePageNavigation(this.pageCount, 'nav-next');
    }

    _handlePrevious() {
        if (this.page <= 1) {
            return;
        }
        var page = this.page - 1;
        this._handlePageNavigation(page, 'nav-prev');
    }

    _handleNext() {
        if (this.page >= this.pageCount) {
            return;
        }
        var page = this.page + 1;
        this._handlePageNavigation(page, 'nav-next');
    }
}
customElements.define('eplib-paginate', EplibPaginate);
